import { useState, useEffect } from 'react';
import { apiGet, ApiError } from '../lib/apiClient';
import { DataTable } from '../components/DataTable';

interface AuditEntry {
  id: string;
  userId: string;
  action: string;
  resourceType: string;
  resourceId: string;
  metadata?: Record<string, unknown>;
  ipAddress: string;
  timestamp: string;
}

interface PaginatedAuditLogs {
  items: AuditEntry[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const DEFAULT_FILTERS = {
  userId: '',
  action: '',
  resourceType: '',
  resourceId: '',
  startDate: '',
  endDate: '',
};

export function AuditLogPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PaginatedAuditLogs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.action) params.set('action', filters.action);
      if (filters.resourceType) params.set('resourceType', filters.resourceType);
      if (filters.resourceId) params.set('resourceId', filters.resourceId);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      params.set('page', String(pageNum));
      params.set('limit', '20');

      const result = await apiGet<PaginatedAuditLogs>(`/audit-logs?${params.toString()}`);
      setData(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = () => {
    if (page === 1) {
      fetchLogs(1);
    } else {
      setPage(1);
    }
  };

  const handleClear = () => {
    setFilters(DEFAULT_FILTERS);
    if (page === 1) {
      fetchLogs(1);
    } else {
      setPage(1);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Audit Log</h1>
          <p>Browse system activity by user, action type, or resource. Filter and page through all recorded events.</p>
        </div>
      </div>

      <div className="section-toolbar" style={{ gap: '0.75rem' }}>
        <input
          placeholder="User ID"
          value={filters.userId}
          onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
        />
        <input
          placeholder="Action"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
        />
        <input
          placeholder="Resource Type"
          value={filters.resourceType}
          onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
        />
        <input
          placeholder="Resource ID"
          value={filters.resourceId}
          onChange={(e) => setFilters({ ...filters, resourceId: e.target.value })}
        />
        <input
          type="date"
          placeholder="Start Date"
          value={filters.startDate}
          onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
        />
        <input
          type="date"
          placeholder="End Date"
          value={filters.endDate}
          onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
        />
        <button className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
        <button className="btn" onClick={handleClear}>
          Clear
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <DataTable
        id="audit-log"
        rows={data?.items}
        loading={loading}
        empty="No audit entries match this filter."
        noun="entries"
        getRowKey={(entry) => entry.id}
        pagination={
          data
            ? {
                page: data.page,
                totalPages: data.totalPages,
                total: data.total,
                hasNext: data.hasNext,
                hasPrev: data.hasPrev,
                onPageChange: setPage,
                noun: 'total',
              }
            : undefined
        }
        columns={[
          {
            key: 'timestamp',
            header: 'Timestamp',
            value: (e) => e.timestamp,
            render: (e) => new Date(e.timestamp).toLocaleString(),
            width: '170px',
          },
          { key: 'userId', header: 'User ID', value: (e) => e.userId },
          { key: 'action', header: 'Action', value: (e) => e.action, filter: 'select' },
          {
            key: 'resource',
            header: 'Resource',
            value: (e) => `${e.resourceType}/${e.resourceId}`,
          },
          { key: 'ip', header: 'IP Address', value: (e) => e.ipAddress },
          {
            key: 'details',
            header: 'Details',
            render: (e) =>
              e.metadata && Object.keys(e.metadata).length > 0 ? (
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxWidth: '360px',
                  }}
                >
                  {JSON.stringify(e.metadata, null, 2)}
                </pre>
              ) : (
                '—'
              ),
          },
        ]}
      />

    </div>
  );
}
